import {
  AddClientModalTab,
  AddClientToOrderModalProps,
} from '@/renderer/types/sales';
import { Loader2, Search, UserRound, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../common';

const emptyClientForm = {
  firstName: '',
  lastName: '',
  address: '',
  email: '',
  contact: '',
  tinNumber: '',
};

export const AddClientToOrderModal: React.FC<AddClientToOrderModalProps> = ({
  isOpen,
  order,
  clients,
  isLoading = false,
  isAssigning = false,
  isCreating = false,
  onClose,
  onAssignClient,
  onCreateClient,
}) => {
  const [activeTab, setActiveTab] = useState<AddClientModalTab>('EXISTING');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formData, setFormData] = useState(emptyClientForm);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('EXISTING');
    setSearchQuery('');
    setFormData(emptyClientForm);
    setSelectedClientId(order?.client_id || '');
  }, [isOpen, order?.client_id]);

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return clients;

    return clients.filter((client) => {
      const name = client.full_name.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      const contact = client.contact?.toLowerCase() || '';
      const address = client.address?.toLowerCase() || '';
      return (
        name.includes(query) ||
        email.includes(query) ||
        contact.includes(query) ||
        address.includes(query)
      );
    });
  }, [clients, searchQuery]);

  const canCreateClient =
    formData.firstName.trim().length > 0 && formData.lastName.trim().length > 0;

  const isBusy = isAssigning || isCreating;

  const handleAssignClient = async () => {
    if (!selectedClientId) return;
    await onAssignClient({ clientId: selectedClientId });
  };

  const handleCreateClient = async () => {
    if (!canCreateClient) return;

    await onCreateClient({
      firstName: formData.firstName,
      lastName: formData.lastName,
      address: formData.address,
      email: formData.email,
      contact: formData.contact,
      tinNumber: formData.tinNumber,
    });
  };

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isBusy ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isBusy) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close add client modal"
      />

      <div className="relative w-full max-w-5xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Add Client Details To Order
          </h2>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Find a client"
                className="h-10 w-48 rounded border border-gray-300 bg-white pl-9 pr-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="px-4 pt-4">
          <div className="grid grid-cols-2 border-b border-gray-300">
            {(['EXISTING', 'NEW'] as AddClientModalTab[]).map((tab) => (
              <button
                key={tab}
                type="button"
                disabled={isBusy}
                onClick={() => setActiveTab(tab)}
                className={`h-12 text-xs font-semibold uppercase tracking-[0.2em] ${
                  activeTab === tab
                    ? 'border-b-2 border-gray-900 text-black'
                    : 'text-black/50'
                }`}
              >
                {tab === 'EXISTING' ? 'Existing Client' : 'Add New Client'}
              </button>
            ))}
          </div>
        </div>

        <div className="h-[420px] overflow-y-auto px-4 pb-4 pt-3">
          {activeTab === 'EXISTING' ? (
            isLoading ? (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-black/70">
                <Loader2 size={16} className="animate-spin" />
                Loading clients...
              </div>
            ) : filteredClients.length === 0 ? (
              <p className="py-8 text-center text-sm text-black/60">
                No clients found
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {filteredClients.map((client) => {
                  const isSelected = selectedClientId === client.id;
                  return (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => setSelectedClientId(client.id)}
                      className={`rounded border p-3 text-left transition-colors ${
                        isSelected
                          ? 'app-bg border-primary'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <p className="text-lg font-semibold uppercase">
                        {client.full_name || 'No Name'}
                      </p>
                      <div className="mt-2 space-y-1 text-sm text-black/80">
                        {client.address ? <p>{client.address}</p> : null}
                        {client.contact ? <p>{client.contact}</p> : null}
                        {client.email ? <p>{client.email}</p> : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>First name</span>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        firstName: event.target.value,
                      }))
                    }
                    placeholder="First name"
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>Last name</span>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        lastName: event.target.value,
                      }))
                    }
                    placeholder="Last name"
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>Address</span>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        address: event.target.value,
                      }))
                    }
                    placeholder="Address"
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>Email address</span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        email: event.target.value,
                      }))
                    }
                    placeholder="email"
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>Contact number</span>
                  <input
                    type="text"
                    value={formData.contact}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        contact: event.target.value,
                      }))
                    }
                    placeholder="+256..."
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>

                <div className="mb-1 block text-sm font-semibold text-black">
                  <span>Tin</span>
                  <input
                    type="text"
                    value={formData.tinNumber}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        tinNumber: event.target.value,
                      }))
                    }
                    placeholder="Client tin number (optional)"
                    disabled={isBusy}
                    className="mt-1 h-11 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-black/70">
            <UserRound size={16} />
            <span>
              BILL #{order.bill_number}{' '}
              {order.client_name ? `• ${order.client_name}` : ''}
            </span>
          </div>

          {activeTab === 'EXISTING' ? (
            selectedClientId ? (
              <Button
                type="button"
                onClick={handleAssignClient}
                disabled={isBusy}
                className="h-10 text-xs font-semibold uppercase tracking-wide"
              >
                {isAssigning ? 'Saving' : 'Save Client To Order'}
              </Button>
            ) : (
              <span className="text-xs font-medium uppercase tracking-wide text-black/50">
                Select a client to continue
              </span>
            )
          ) : (
            <Button
              type="button"
              onClick={handleCreateClient}
              disabled={isBusy || !canCreateClient}
              className="h-10 text-xs font-semibold uppercase tracking-wide"
            >
              {isCreating ? 'Creating' : 'Add Client'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
